<template>
  <v-container fluid class="ma-0 pa-0">
    <v-app-bar v-if="showAppBar" app class="primary">
      <v-icon color="white" large>menu_book</v-icon>
      <v-toolbar-title class="white--text pl-3"
        >Study Dataset Summary
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip bottom color="primary">
        <template v-slot:activator="{ on: tooltip }">
          <v-btn color="primary--text" @click="goBack()" v-on="{ ...tooltip }">
            <v-icon left>library_books</v-icon>
            Saved Study Datasets
          </v-btn>
        </template>
        <span>Return to Saved Study Datasets</span>
      </v-tooltip>
    </v-app-bar>

    <v-container v-if="isLoading" class="ma-0 pa-2">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
      </v-row>
    </v-container>

    <v-container v-else fluid class="ma-0 pa-3">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="pa-3 ma-0">
            <v-row class="ma-0 pa-0">
              <span class="title">{{ collection.label }}</span>
            </v-row>

            <v-divider class="ma-0 pa-0 my-2"></v-divider>

            <v-row class="ma-0 pa-0">
              <span class="text-subtitle-1"
                ><b>Created by: </b>{{ collection.user_name }}</span
              >
            </v-row>

            <v-row class="ma-0 pa-0">
              <span class="text-subtitle-1"
                ><b>Created on: </b
                >{{ collection.date_generated_epoch | formatDate }}</span
              >
            </v-row>

            <v-row class="ma-0 pa-0">
              <span class="text-subtitle-1"
                ><b>Shared with: </b> {{ getGroupName(collection.group_id) }}
              </span>
            </v-row>

            <v-divider class="ma-0 pa-0 my-2"></v-divider>

            <v-row class="ma-0 pa-0 pt-2">
              <v-col class="ma-0 pa-0">
                <div class="ma-0 pa-0" style="display: flex;" align="center">
                  <v-tooltip v-if="collection.is_public" top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="green lighten-4"
                        >
                          <v-icon center>mdi-lock-open-variant-outline</v-icon>
                        </v-chip>
                        <span class="black--text text-body-1"> Public </span>
                      </span>
                    </template>
                    <span> Study dataset is public </span>
                  </v-tooltip>

                  <v-tooltip v-else top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="red lighten-4"
                        >
                          <v-icon center>mdi-lock-outline</v-icon>
                        </v-chip>
                        <span class="black--text text-body-1"> Private </span>
                      </span>
                    </template>
                    <span> Study dataset is private </span>
                  </v-tooltip>

                  <v-tooltip v-if="collection.longitudinal" top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-3 mr-2"
                          color="green lighten-4"
                        >
                          L
                        </v-chip>
                        <span class="black--text text-body-1">
                          Longitudinal
                        </span>
                      </span>
                    </template>
                    <span> Study dataset is a longitudinal dataset </span>
                  </v-tooltip>

                  <v-tooltip v-else top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-3 mr-2"
                          color="blue lighten-4"
                        >
                          CS
                        </v-chip>
                        <span class="black--text text-body-1">
                          Cross-sectional
                        </span>
                      </span>
                    </template>
                    <span> Study dataset is a cross-sectional dataset </span>
                  </v-tooltip>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          color="primary"
                          class="white--text title mr-2"
                        >
                          {{
                            collection.subject_variables.length +
                              collection.observation_variables.length
                          }}
                        </v-chip>
                        <span class="black--text text-body-1"> Variables </span>
                      </span>
                    </template>
                    <span>
                      Study dataset contains
                      {{
                        collection.subject_variables.length +
                          collection.observation_variables.length
                      }}
                      variables
                    </span>
                  </v-tooltip>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          color="primary"
                          class="white--text title mr-2"
                        >
                          {{ collection.num_subjects }}
                        </v-chip>
                        <span class="black--text text-body-1">
                          Population
                        </span>
                      </span>
                    </template>
                    <span>
                      Study dataset contains
                      {{ collection.num_subjects }} subjects in study population
                    </span>
                  </v-tooltip>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          color="primary"
                          class="white--text title mr-2"
                        >
                          {{ collection.cohorts.length }}
                        </v-chip>
                        <span class="black--text text-body-1">
                          Study Groups
                        </span>
                      </span>
                    </template>
                    <span>
                      Study dataset contains
                      {{ collection.cohorts.length }} study groups
                    </span>
                  </v-tooltip>

                  <v-spacer />

                  <!-- NOTE: Until correlation summary is implemented, there is no reason to unselect variables. -->
                  <!-- <v-switch
                    v-model="enableVariableSelectionSelect"
                    label="Enable variable selection"
                    class="pa-0 ma-0 pr-6"
                    hide-details
                  ></v-switch> -->

                  <v-switch
                    v-model="useLongScaleNamesSelect"
                    label="Use long variable names"
                    class="pa-0 ma-0 ml-2"
                    hide-details
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="ma-0 pa-0 mt-4">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="ma-0 pa-0">
            <v-tabs v-model="summaryTab" light class="ma-0 pa-0">
              <v-tab key="variables"><span>Variables</span></v-tab>
              <v-tab key="datasets"><span>Source Datasets</span></v-tab>
              <v-tab
                key="visits"
                :disabled="
                  !collection.longitudinal || !collection.has_visits_set
                "
              >
                <span>Visits</span>
              </v-tab>
              <v-tab key="cohorts" :disabled="collection.cohorts.length == 0"
                ><span>Study Groups</span></v-tab
              >
              <v-tab key="dashboard"><span>Data Dashboard</span></v-tab>
              <v-tab key="correlation"><span>Correlation Summary</span></v-tab>
            </v-tabs>

            <v-tabs-items v-model="summaryTab" class="px-3 pb-3 ma-0">
              <v-tab-item key="variables" :eager="true">
                <variable-table />
              </v-tab-item>

              <v-tab-item key="datasets" :eager="true">
                <dataset-table />
              </v-tab-item>

              <v-tab-item key="visits" :eager="true">
                <visit-summary />
              </v-tab-item>

              <v-tab-item key="cohorts" :eager="true">
                <cohort-table
                  :cohorts="collection.cohorts"
                  :show-title-bar="false"
                  :show-colors="true"
                  disable-pagination
                  @cohortColorChange="updateCohortColor"
                />
              </v-tab-item>

              <!-- deliberately not eager: the dashboard computes on mount -->
              <v-tab-item key="dashboard"> <data-dashboard /> </v-tab-item>

              <!-- not eager either: the matrix is computed on mount -->
              <v-tab-item key="correlation">
                <correlation-summary />
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>

      <!-- define patterns -->
      <svg width="1" height="1" class="pa-0 ma-0">
        <defs>
          <pattern
            v-for="(p, index) in patterns"
            :id="p['id']"
            :key="'dep-' + index"
            patternUnits="userSpaceOnUse"
            width="25"
            height="25"
            :patternTransform="p['transform']"
          >
            <g v-html="p['pattern']" />
          </pattern>
        </defs>
      </svg>
    </v-container>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetSummary/types';
import { state as authState } from '@/store/modules/auth/types';
import CohortTable from '@/components/common/CohortTable.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import DatasetTable from '@/components/DatasetSummary/DatasetTable.vue';
import VariableTable from '@/components/DatasetSummary/VariableTable.vue';
import VisitSummary from '@/components/DatasetSummary/VisitSummary.vue';
import DataDashboard from '@/components/DatasetSummary/DataDashboard/DataDashboard.vue';
import CorrelationSummary from '@/components/DatasetSummary/Correlations/CorrelationSummary.vue';
import { patterns } from '@/utils/patterns';

export default {
  filters: {
    formatDate(ts) {
      // note that toISOString is going to give us UTC
      return new Date(ts).toISOString().substr(0, 10);
    },
  },
  components: {
    LoadingSpinner,
    CorrelationSummary,
    DataDashboard,
    DatasetTable,
    VariableTable,
    CohortTable,
    VisitSummary,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    showAppBar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      patterns: patterns,
      summaryTab: 0,
      gid2group: {},
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      collection: state.COLLECTION,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    ...mapState('auth', {
      user: authState.USER,
    }),
    useLongScaleNamesSelect: {
      get() {
        return this.useLongScaleNames;
      },
      set(value) {
        this.setUseLongScaleNames(value);
      },
    },
  },
  watch: {
    id() {
      this.initialize();
    },
  },
  async created() {
    this.initialize();
  },
  mounted() {
    let g2n = {};
    this.user.groups.forEach(g => {
      g2n[g.group_id] = g;
    });
    this.gid2group = g2n;
  },
  methods: {
    ...mapActions('datasetSummary', {
      fetchCollection: actions.FETCH_COLLECTION,
      setIsCollection: actions.SET_IS_COLLECTION,
      setUseLongScaleNames: actions.SET_USE_LONG_SCALE_NAMES,
      resetAll: actions.RESET_ALL,
    }),
    async initialize() {
      this.isLoading = true;

      this.summaryTab = 0;
      this.useLongScaleNamesSelect = false;

      await this.resetAll();
      await this.setIsCollection(true);
      await this.fetchCollection(this.id);

      this.isLoading = false;
    },
    goBack() {
      this.$router.push('/study_datasets');
    },
    updateCohortColor(cc) {
      this.updateCohortColorInArray(this.collection.cohorts, cc);
    },
    updateCohortColorInArray(a, cc) {
      let i = 0;
      let cnum = null;
      a.forEach(c => {
        if (c.id == cc['cohort'].id) {
          cnum = i;
        }
        i += 1;
      });
      if (cnum != null) {
        a[cnum]['color'] = cc['color'];
        if ('pattern' in cc) {
          a[cnum]['pattern'] = cc['pattern'];
        }
        // force update
        a.splice(cnum, 1, a[cnum]);
      }
    },
    getGroupName(gid) {
      if (gid === null) return '-';
      return gid in this.gid2group ? this.gid2group[gid].name : 'group ' + gid;
    },
  },
};
</script>

<style scoped></style>
